/* ==========================================================================
   hbc-header.js — Hout Bay Cycle Co header scroll state
   --------------------------------------------------------------------------
   Single job: flip the header's scroll state on .header-wrapper once the page
   has scrolled past a short threshold, so the transparent overlay header on
   the homepage becomes the solid compact header. Two hooks are emitted and
   kept in lockstep, per the agreed contract with the search layer:

     class="hbc-header--stuck"           present once scrolled
     data-hbc-header-state="overlay"     transparent over the hero
     data-hbc-header-state="solid"       otherwise (incl. every non-home page)

   Both live on `.header-wrapper` — the <sticky-header> element — which is an
   ANCESTOR of .header, .header__icons and .header__search, so descendant
   selectors from either hook reach the whole header including search.

   Deliberately does NOT:
     - listen to `scroll` (an IntersectionObserver on a sentinel costs nothing
       per frame, a scroll handler costs something on every one)
     - write inline styles (all visual state lives in hbc-header.css, so the
       transition is a single class flip and the design stays overridable)
     - measure or set any height (Dawn's own StickyHeader already publishes
       --header-height, and the band height is a CSS token)

   Load with `defer`. See hbc-header-INTEGRATION.md §2.
   ========================================================================== */

(function () {
  'use strict';

  var STUCK = 'hbc-header--stuck';
  var OVERLAY = 'hbc-header--overlay';
  var STATE_ATTR = 'data-hbc-header-state';
  var BOOTING = 'hbc-header-booting';
  var SENTINEL_ID = 'hbc-header-sentinel';
  var THRESHOLD_PX = 24; // how far you scroll before the header goes solid

  var isStuck = false;
  var observer = null;

  function headers() {
    return document.querySelectorAll('.hbc-header');
  }

  function apply(stuck) {
    isStuck = stuck;
    var list = headers();
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      el.classList.toggle(STUCK, stuck);
      // Mirror of the class, for hbc-search.css and anything else that wants
      // to hook the state without depending on our class names. Only the
      // homepage header can ever be "overlay"; everywhere else it is always
      // "solid", and Liquid has already written that in — so this only ever
      // flips the value on a header that carries the overlay class.
      if (el.classList.contains(OVERLAY)) {
        el.setAttribute(STATE_ATTR, stuck ? 'solid' : 'overlay');
      } else {
        el.setAttribute(STATE_ATTR, 'solid');
      }
    }
  }

  function sentinel() {
    var el = document.getElementById(SENTINEL_ID);
    if (el) return el;

    el = document.createElement('div');
    el.id = SENTINEL_ID;
    el.setAttribute('aria-hidden', 'true');
    // Absolutely positioned, so it is not a grid item on <body> and cannot
    // affect layout. top:0 resolves against the initial containing block,
    // i.e. the very top of the document.
    //
    // `display:block` is NOT redundant. Dawn's base.css line ~468 ships
    //   a:empty, ul:empty, div:empty, section:empty, ... { display: none; }
    // so any element created empty is hidden, and a hidden sentinel never
    // intersects — the header would be stuck-on-load, permanently. The inline
    // declaration outranks that rule (which carries no !important).
    el.style.cssText =
      'display:block;position:absolute;top:0;left:0;width:1px;height:' +
      THRESHOLD_PX +
      'px;opacity:0;pointer-events:none;';
    document.body.insertBefore(el, document.body.firstChild);
    return el;
  }

  function start() {
    if (!headers().length) return;

    // Paint the correct state first, then allow transitions, so a reload
    // half-way down the page does not animate the header on arrival.
    document.documentElement.classList.add(BOOTING);

    if (!('IntersectionObserver' in window)) {
      // Ancient browser: solid header everywhere. Degrades to readable, not broken.
      apply(true);
      release();
      return;
    }

    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      function (entries) {
        apply(!entries[0].isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel());
    release();
  }

  function release() {
    // Two frames: one for the observer's first callback to land, one for the
    // browser to paint it, then transitions are re-enabled.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.remove(BOOTING);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Theme editor: the section's DOM is replaced on every settings change, so
  // re-apply the state we already know about instead of waiting for a scroll.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('.hbc-header')) {
      apply(isStuck);
    }
  });
})();
